import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faImage, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Dialog } from './ui/Dialog';
import { Button } from './ui/Button';
import { getCookie } from '../../components/apiClient';
import { apiJson } from '../../components/apiClient';

const CRITERIOS = [
  ['comprimento', 'Pelo menos 8 caracteres'],
  ['maiusculas', 'Pelo menos 2 letras maiúsculas'],
  ['minusculas', 'Pelo menos 2 letras minúsculas'],
  ['numeros', 'Pelo menos 2 números'],
  ['especiais', 'Pelo menos 2 caracteres especiais (!@#$%&*()_;)'],
  ['iguais', 'Senhas iguais'],
];

function verificarCriterios(senha, confirmacao) {
  return {
    comprimento: senha.length >= 8,
    maiusculas: /[A-Z].*[A-Z]/.test(senha),
    minusculas: /[a-z].*[a-z]/.test(senha),
    numeros: /\d.*\d/.test(senha),
    especiais: /[!@#$%&*()_;].*[!@#$%&*()_;]/.test(senha),
    iguais: senha === confirmacao && confirmacao.length > 0,
  };
}

// Configurações do usuário, versão nativa do CRM — substitui o
// `UserProfileModal` do Cofre (Modais/Modal.jsx) para quem acessa via
// Sidebar → Configurações. Cofre e CRM têm identidade visual separada
// (não misturar navegação/menus/casca entre os dois produtos), então o
// CRM precisa da própria tela em vez de reutilizar o modal do Cofre —
// mesmo contrato de API (`/api/usuarios/buscar` e `/api/usuarios/atualizar`,
// backend da plataforma/Cofre, que também é quem guarda a conta do
// usuário), só a casca visual muda para os primitivos do design system
// (Dialog, Button, tokens `--crm-*`).
function SettingsDialog({ open, onClose, showToast, onLogoAlterada }) {
  const [userData, setUserData] = useState(null);
  const [aba, setAba] = useState('perfil');
  const [logo, setLogo] = useState(null);
  const [salvandoLogo, setSalvandoLogo] = useState(false);
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAba('perfil');
    setSenhaAtual('');
    setNovaSenha('');
    setConfirmarSenha('');
    (async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/buscar`, {
          method: 'GET',
          headers: { Authority: getCookie('info') },
        });
        const result = await response.json();
        if (result.status === 'ok') {
          setUserData(result.usuario);
        } else {
          showToast(result.mensagem || 'Erro ao carregar dados do usuário', 'error');
        }
      } catch (error) {
        showToast('Erro ao carregar dados do usuário', 'error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const criterios = verificarCriterios(novaSenha, confirmarSenha);
  const tudoOk = Object.values(criterios).every(Boolean);

  const salvarSenha = async (e) => {
    e.preventDefault();
    if (!tudoOk) { showToast('Preencha todos os requisitos de senha', 'error'); return; }
    setSalvando(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/atualizar`, {
        method: 'POST',
        headers: { Authority: getCookie('info'), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: userData.nome,
          email: userData.email,
          cargo: userData.cargo,
          senhaAtual,
          senhaNova: novaSenha,
          senhaNovaConfirm: novaSenha,
          id: userData.id,
        }),
      });
      const result = await response.json();
      if (result.status === 'ok') {
        showToast('Senha alterada com sucesso!', 'success');
        setSenhaAtual(''); setNovaSenha(''); setConfirmarSenha(''); setAba('perfil');
      } else {
        showToast(result.mensagem || 'Erro ao alterar senha', 'error');
      }
    } catch (error) {
      showToast('Erro ao alterar senha', 'error');
    } finally {
      setSalvando(false);
    }
  };

  const iniciais = userData?.nome
    ? (userData.nome.split(' ').filter(Boolean).length > 1
      ? `${userData.nome.split(' ')[0][0]}${userData.nome.split(' ').filter(Boolean).pop()[0]}`
      : userData.nome[0]).toUpperCase()
    : '';

  // Só quem administra a empresa troca a marca — ela é vista por todos os
  // usuários do tenant, não é preferência pessoal. O backend recusa de novo
  // (403); isto aqui só evita mostrar um controle que não funcionaria.
  const podeEditarLogo = userData?.cargo === 'Admin' || userData?.cargo === 'root' || userData?.is_super_admin;

  useEffect(() => {
    if (!open) return;
    apiJson('/modulos/logo')
      .then((r) => { if (r.status === 'ok') setLogo(r.logo || null); })
      .catch(() => {});
  }, [open]);

  const salvarLogo = async (dataUri) => {
    setSalvandoLogo(true);
    try {
      const r = await apiJson('/modulos/logo', { method: 'POST', body: JSON.stringify({ logo: dataUri }) });
      if (r.status !== 'ok') throw new Error(r.mensagem || 'Não consegui salvar a logo.');
      setLogo(r.logo || null);
      if (onLogoAlterada) onLogoAlterada(r.logo || null);
      showToast(dataUri ? 'Logo atualizada.' : 'Logo removida.', 'success');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSalvandoLogo(false);
    }
  };

  const aoEscolherLogo = (e) => {
    const arquivo = e.target.files?.[0];
    e.target.value = '';  // permite reescolher o MESMO arquivo depois
    if (!arquivo) return;
    // Checagem no cliente antes de ler: evita carregar 10 MB na memória do
    // navegador só para o servidor recusar em seguida.
    if (arquivo.size > 300 * 1024) {
      showToast('Imagem muito grande — use um arquivo de até 300 KB.', 'error');
      return;
    }
    const leitor = new FileReader();
    leitor.onload = () => salvarLogo(String(leitor.result));
    leitor.onerror = () => showToast('Não consegui ler esse arquivo.', 'error');
    leitor.readAsDataURL(arquivo);
  };

  return (
    <Dialog open={open} onClose={onClose} title="Configurações" width={420}>
      {!userData ? (
        <div className="crm-form-hint">Carregando...</div>
      ) : aba === 'perfil' ? (
        <>
          <div className="crm-settings-avatar-row">
            <div className="crm-settings-avatar">{iniciais}</div>
            <div>
              <div className="crm-settings-name">{userData.nome}</div>
              <div className="crm-form-hint">{userData.cargo === 'root' ? 'Admin' : userData.cargo}</div>
            </div>
          </div>
          <div className="crm-settings-info-item">
            <FontAwesomeIcon icon={faEnvelope} />
            <div>
              <div className="crm-form-hint">Email</div>
              <div>{userData.email}</div>
            </div>
          </div>
          <Button variant="outline" icon={faLock} onClick={() => setAba('senha')}>
            Alterar senha
          </Button>

          {podeEditarLogo && (
            <div className="crm-settings-secao">
              <div className="crm-settings-secao-titulo">Logo da empresa</div>
              <div className="crm-logo-upload">
                <div className="crm-logo-preview">
                  {logo
                    ? <img src={logo} alt="Logo da empresa" />
                    : <FontAwesomeIcon icon={faImage} className="crm-logo-preview-vazio" />}
                </div>
                <div className="crm-logo-acoes">
                  <input
                    id="crm-logo-input"
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    style={{ display: 'none' }}
                    onChange={aoEscolherLogo}
                  />
                  <Button
                    variant="outline"
                    icon={faImage}
                    disabled={salvandoLogo}
                    onClick={() => document.getElementById('crm-logo-input')?.click()}
                  >
                    {logo ? 'Trocar imagem' : 'Enviar imagem'}
                  </Button>
                  {logo && (
                    <Button variant="ghost" icon={faTrash} disabled={salvandoLogo} onClick={() => salvarLogo(null)}>
                      Remover
                    </Button>
                  )}
                  <span className="crm-logo-hint">PNG, JPEG, SVG ou WebP · até 300 KB</span>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <form onSubmit={salvarSenha} className="crm-form">
          <div className="crm-form-field">
            <label className="crm-form-label">Senha atual</label>
            <input type="password" className="crm-input" value={senhaAtual} onChange={(e) => setSenhaAtual(e.target.value)} maxLength={50} required />
          </div>
          <div className="crm-form-field">
            <label className="crm-form-label">Nova senha</label>
            <input type="password" className="crm-input" value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} maxLength={50} required />
          </div>
          <div className="crm-form-field">
            <label className="crm-form-label">Confirmar nova senha</label>
            <input type="password" className="crm-input" value={confirmarSenha} onChange={(e) => setConfirmarSenha(e.target.value)} maxLength={50} required />
          </div>
          <ul className="crm-settings-checklist">
            {CRITERIOS.map(([chave, label]) => (
              <li key={chave} className={criterios[chave] ? 'ok' : ''}>{label}</li>
            ))}
          </ul>
          <div className="crm-modal-footer" style={{ padding: 0 }}>
            <Button variant="ghost" type="button" onClick={() => setAba('perfil')} disabled={salvando}>Voltar</Button>
            <Button variant="primary" type="submit" loading={salvando} disabled={!tudoOk}>Salvar</Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

export default SettingsDialog;
